import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkCjkStrong from '../../utils/remarkCjkStrong';
import CodeBlock from './CodeBlock';
import { extractDocMarkers } from '../../utils/docMarkers';
import { safeUrl } from '../../utils/safeUrl';
import { IconDownload, IconFileText } from '@tabler/icons-react';

const inlineCodeStyle = {
  background: 'var(--gw-gray-soft, #f1f1ef)',
  padding: '2px 5px',
  borderRadius: '4px',
  fontSize: '13px',
  fontFamily: 'monospace',
  wordBreak: 'break-all',
};

const markdownComponents = {
  a({ href, children, ...props }) {
    const url = safeUrl(href);
    if (!url) return <span>{children}</span>;
    return (
      <a {...props} href={url} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  },
  code({ inline, className, children, ...props }) {
    const match = /language-([\w-]+)/.exec(className || '');
    const code = String(children).replace(/\n$/, '');
    if (!inline && match) {
      return <CodeBlock code={code} lang={match[1]} />;
    }
    if (!inline && code.includes('\n')) {
      return <CodeBlock code={code} lang="" />;
    }
    return (
      <code {...props} className={className} style={inlineCodeStyle}>
        {children}
      </code>
    );
  },
  // 서버가 직접 적는 "반영하지 못한 것" 안내가 눈에 띄어야 한다 — 기본 blockquote 는
  // 들여쓰기만 돼서 본문에 묻힌다(2026-08-20).
  blockquote({ children, ...props }) {
    return (
      <blockquote
        {...props}
        style={{
          margin: "0 0 14px", padding: "10px 14px", borderLeft: "3px solid var(--gw-border, #e5e5e2)",
          background: "transparent", borderRadius: "0 6px 6px 0", fontSize: "14px",
        }}
      >
        {children}
      </blockquote>
    );
  },
  p({ children, ...props }) {
    return <p {...props} style={{ margin: '0 0 14px', fontSize: '14px', lineHeight: 1.8 }}>{children}</p>;
  },
  h1({ children, ...props }) {
    return <h1 {...props} style={{ fontSize: '20px', fontWeight: 700, margin: '6px 0 14px', lineHeight: 1.35 }}>{children}</h1>;
  },
  h2({ children, ...props }) {
    return <h2 {...props} style={{ fontSize: '17px', fontWeight: 700, margin: '24px 0 10px', lineHeight: 1.35 }}>{children}</h2>;
  },
  h3({ children, ...props }) {
    return <h3 {...props} style={{ fontSize: '15px', fontWeight: 650, margin: '20px 0 8px', lineHeight: 1.4 }}>{children}</h3>;
  },
  ul({ children, ...props }) {
    return <ul {...props} style={{ margin: '0 0 14px', paddingLeft: '24px', fontSize: '14px', lineHeight: 1.8 }}>{children}</ul>;
  },
  ol({ children, ...props }) {
    return <ol {...props} style={{ margin: '0 0 14px', paddingLeft: '24px', fontSize: '14px', lineHeight: 1.8 }}>{children}</ol>;
  },
  li({ children, ...props }) {
    return <li {...props} style={{ margin: '5px 0' }}>{children}</li>;
  },
};

export default function MessageContent({ content: rawContent, onDownloadDoc }) {
  const { docs, content } = extractDocMarkers(rawContent);

  return (
    <>
      <div style={{ whiteSpace: 'normal', wordBreak: 'break-word', overflowWrap: 'anywhere', fontFamily: 'var(--gw-font)', color: 'var(--text)' }}>
        <ReactMarkdown remarkPlugins={[remarkGfm, remarkCjkStrong]} components={markdownComponents}>
          {content}
        </ReactMarkdown>
      </div>
      {docs.map((d, k) => (
        <div
          key={`doc-${k}`}
          onClick={() => onDownloadDoc(d.id, d.name)}
          style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '12px', padding: '10px 12px', background: '#f8fafc', border: '1px solid var(--border)', borderRadius: '10px', cursor: 'pointer', width: '100%', maxWidth: '360px', boxSizing: 'border-box', minWidth: 0, fontFamily: 'var(--gw-font)' }}
          title="클릭하여 문서를 다운로드합니다"
        >
          <IconFileText size={24} stroke={1.7} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.name}</div>
            <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Word 문서 · 클릭하여 다운로드</div>
          </div>
          <IconDownload size={17} stroke={1.8} color="var(--accent)" aria-hidden="true" style={{ flexShrink: 0 }} />
        </div>
      ))}
    </>
  );
}
